import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useFeatures } from '@/lib/useFeatures';
import {
  Library, Sparkles, Flame, BookOpen, Radio, Camera,
  ChevronDown, ArrowUpRight, FileText, PenTool, Users,
  MessageCircle, Headphones, Shield, Bookmark, NotebookPen, Phone,
} from 'lucide-react';

/**
 * Premium "Resources" dropdown menu for the Header.
 *
 * Sections:
 *  - Featured: Resources Hub + Bookshelf (always-on anchors).
 *  - Signature Tools: only the feature-flagged tools currently enabled.
 *  - Library: every item from the live /api/resources feed so nothing is overlooked.
 *
 * Behaviour:
 *  - Hover/click to open on desktop, click to expand on mobile.
 *  - Closes on outside click, Esc, route change, or item selection.
 *  - Panel grows in height with content; max-height bound so it stays usable on small screens.
 */

const ICON_MAP = {
  shield: Shield,
  'file-text': FileText,
  headphones: Headphones,
  'book-open': BookOpen,
  'pen-tool': PenTool,
  users: Users,
  bookmark: Bookmark,
  'message-circle': MessageCircle,
  sparkles: Sparkles,
  phone: Phone,
  'notebook-pen': NotebookPen,
};

const FEATURE_ITEMS = [
  { slug: 'oracle', label: 'The Oracle', desc: 'Ask the book anything. Sourced + cited.', to: '/oracle', Icon: Sparkles },
  { slug: 'forge', label: 'The Forge', desc: 'Burn a limiting belief. Rebuild it in gold.', to: '/forge', Icon: Flame },
  { slug: 'cinematic_read', label: 'Cinematic Read', desc: 'Lessons that score themselves.', to: '/courses/headspace', Icon: BookOpen },
  { slug: 'pirate_radio', label: 'Pirate Radio', desc: '24/7 station with an AI DJ.', to: '/radio', Icon: Radio },
  { slug: 'proof_of_life', label: 'Proof of Life', desc: 'One photo a day. A 90-day mosaic of you.', to: '/proof', Icon: Camera },
];

// Module-cache so we don't refetch on every header mount.
let _resourcesCache = null;
let _resourcesInflight = null;
const fetchResources = async () => {
  if (_resourcesCache) return _resourcesCache;
  if (_resourcesInflight) return _resourcesInflight;
  _resourcesInflight = api.get('/resources')
    .then((r) => { _resourcesCache = Array.isArray(r.data) ? r.data : []; return _resourcesCache; })
    .catch(() => { _resourcesCache = []; return _resourcesCache; })
    .finally(() => { _resourcesInflight = null; });
  return _resourcesInflight;
};

const Row = ({ Icon, label, desc, to, accent = 'subtle', external = false, testid }) => {
  const inner = (
    <>
      <span className={`mt-0.5 flex-shrink-0 w-8 h-8 rounded-full inline-flex items-center justify-center transition-colors ${
        accent === 'gold'
          ? 'bg-[color:var(--kc-gold)]/15 border border-[color:var(--kc-gold)]/40 text-[color:var(--kc-gold)] group-hover:scale-110'
          : 'bg-[color:var(--kc-gold)]/10 border border-[color:var(--kc-gold)]/30 text-[color:var(--kc-gold)] group-hover:bg-[color:var(--kc-gold)]/20'
      }`}>
        <Icon size={14}/>
      </span>
      <div className="min-w-0 flex-1">
        <div className="font-display uppercase text-sm tracking-wide leading-tight text-white group-hover:text-[color:var(--kc-gold)] transition-colors inline-flex items-center gap-1">
          {label}
          {external && <ArrowUpRight size={11} className="opacity-50" />}
        </div>
        {desc && <div className="text-xs text-white/55 mt-0.5 leading-snug line-clamp-2">{desc}</div>}
      </div>
    </>
  );
  const cls = "group block px-3 py-2.5 rounded-[var(--kc-radius-sm)] hover:bg-white/[0.06] transition-colors";
  if (external) {
    return <a href={to} target="_blank" rel="noreferrer" className={cls} data-testid={testid}><div className="flex items-start gap-3">{inner}</div></a>;
  }
  if (to.startsWith('tel:') || to.startsWith('mailto:')) {
    return <a href={to} className={cls} data-testid={testid}><div className="flex items-start gap-3">{inner}</div></a>;
  }
  return <Link to={to} className={cls} data-testid={testid}><div className="flex items-start gap-3">{inner}</div></Link>;
};

export const ResourcesMenu = ({ mobile = false, onSelect }) => {
  const { isOn } = useFeatures();
  const [open, setOpen] = useState(false);
  const [resources, setResources] = useState(_resourcesCache || []);
  const wrapperRef = useRef(null);
  const closeTimerRef = useRef(null);

  // Pull resources on first mount (cached after).
  useEffect(() => {
    if (_resourcesCache) { setResources(_resourcesCache); return; }
    let mounted = true;
    fetchResources().then((rows) => { if (mounted) setResources(rows || []); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (mobile) return;
    if (!open) return;
    const onDoc = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, mobile]);

  const scheduleClose = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setOpen(false), 120);
  };
  const cancelClose = () => { if (closeTimerRef.current) clearTimeout(closeTimerRef.current); };

  const enabledFeatures = FEATURE_ITEMS.filter((f) => isOn(f.slug));

  // Build the library list from API + always include Bookshelf as a fallback anchor.
  const libraryItems = (() => {
    const out = [];
    for (const r of resources) {
      if (!r?.title) continue;
      const href = r.cta_href || '#';
      if (!href || href === '#') continue;
      const Icon = ICON_MAP[r.icon] || FileText;
      out.push({
        key: r.id || r.title,
        label: r.title,
        desc: r.description || r.category || '',
        href,
        Icon,
        external: !(href.startsWith('/') || href.startsWith('tel:') || href.startsWith('mailto:')),
      });
    }
    // Ensure Bookshelf is present even if not in the resources feed.
    if (!out.some((it) => it.href === '/bookshelf')) {
      out.unshift({ key: 'bookshelf', label: 'Bookshelf', desc: "Books Mickey leans on, and why.", href: '/bookshelf', Icon: BookOpen, external: false });
    }
    return out;
  })();

  // =============== MOBILE: expandable accordion ===============
  if (mobile) {
    return (
      <div data-testid="resources-menu-mobile">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full py-2 flex items-center justify-between gap-2 font-medium"
          aria-expanded={open}
          data-testid="resources-menu-mobile-toggle"
        >
          <span className="flex items-center gap-2"><Library size={16}/> Resources</span>
          <ChevronDown size={16} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div className="ml-6 mt-1 mb-2 border-l border-kc pl-3 space-y-0.5 max-h-[60vh] overflow-y-auto">
            <Link
              to="/resources"
              onClick={() => { setOpen(false); onSelect?.(); }}
              className="py-1.5 flex items-center gap-2 text-sm text-ink-2 hover:text-gold transition-colors font-semibold"
              data-testid="resources-mobile-hub"
            >
              <Library size={14} className="text-gold"/> Resources Hub
            </Link>

            {enabledFeatures.length > 0 && (
              <div className="pt-2 mt-1 border-t border-kc">
                <div className="text-[10px] tracking-[0.18em] uppercase text-muted-kc py-1">Signature Tools</div>
                {enabledFeatures.map((it) => (
                  <Link
                    key={it.slug}
                    to={it.to}
                    onClick={() => { setOpen(false); onSelect?.(); }}
                    className="py-1.5 flex items-center gap-2 text-sm text-ink-2 hover:text-gold transition-colors"
                    data-testid={`resources-mobile-feature-${it.slug}`}
                  >
                    <it.Icon size={14} className="text-[color:var(--kc-gold)]" /> {it.label}
                  </Link>
                ))}
              </div>
            )}

            {libraryItems.length > 0 && (
              <div className="pt-2 mt-1 border-t border-kc">
                <div className="text-[10px] tracking-[0.18em] uppercase text-muted-kc py-1">Library</div>
                {libraryItems.map((it) => {
                  const isExt = it.external;
                  if (isExt) {
                    return (
                      <a key={it.key} href={it.href} target="_blank" rel="noreferrer" onClick={() => { setOpen(false); onSelect?.(); }} className="py-1.5 flex items-center gap-2 text-sm text-ink-2 hover:text-gold transition-colors" data-testid={`resources-mobile-lib-${it.key}`}>
                        <it.Icon size={14} className="text-gold/80" /> {it.label}
                      </a>
                    );
                  }
                  if (it.href.startsWith('tel:') || it.href.startsWith('mailto:')) {
                    return (
                      <a key={it.key} href={it.href} onClick={() => { setOpen(false); onSelect?.(); }} className="py-1.5 flex items-center gap-2 text-sm text-ink-2 hover:text-gold transition-colors" data-testid={`resources-mobile-lib-${it.key}`}>
                        <it.Icon size={14} className="text-gold/80" /> {it.label}
                      </a>
                    );
                  }
                  return (
                    <Link key={it.key} to={it.href} onClick={() => { setOpen(false); onSelect?.(); }} className="py-1.5 flex items-center gap-2 text-sm text-ink-2 hover:text-gold transition-colors" data-testid={`resources-mobile-lib-${it.key}`}>
                      <it.Icon size={14} className="text-gold/80" /> {it.label}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // =============== DESKTOP: hover dropdown panel ===============
  return (
    <div
      ref={wrapperRef}
      className="relative"
      onMouseEnter={() => { cancelClose(); setOpen(true); }}
      onMouseLeave={scheduleClose}
      data-testid="resources-menu"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`hover:text-gold transition-colors inline-flex items-center gap-1 ${open ? 'text-gold' : ''}`}
        data-testid="resources-menu-trigger"
      >
        Resources <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && <span className="absolute left-0 right-0 top-full h-2" aria-hidden />}

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 z-50 w-[720px] origin-top-right kc-fade-up"
          data-testid="resources-menu-panel"
        >
          <div className="rounded-[var(--kc-radius-lg,18px)] bg-[#0b0b0b] text-white border border-[color:var(--kc-gold)]/30 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.55),0_0_0_1px_rgba(212,164,55,0.08)] overflow-hidden">
            {/* Gold accent bar */}
            <div className="h-[2px] bg-gradient-to-r from-[color:var(--kc-gold)] via-[color:var(--kc-gold-2)] to-transparent" aria-hidden />

            {/* Featured hub strip */}
            <Link
              to="/resources"
              onClick={() => setOpen(false)}
              className="block px-5 py-3 bg-gradient-to-r from-[color:var(--kc-gold)]/10 to-transparent hover:from-[color:var(--kc-gold)]/15 transition-colors group"
              data-testid="resources-menu-hub"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-full bg-[color:var(--kc-gold)]/15 border border-[color:var(--kc-gold)]/50 inline-flex items-center justify-center text-[color:var(--kc-gold)]">
                    <Library size={16}/>
                  </span>
                  <div>
                    <div className="font-display uppercase text-base tracking-wide text-white group-hover:text-[color:var(--kc-gold)] transition-colors">Resources Hub</div>
                    <div className="text-xs text-white/55">Free tools, downloads, audio, and accountability.</div>
                  </div>
                </div>
                <ArrowUpRight size={16} className="text-[color:var(--kc-gold)] opacity-70 group-hover:opacity-100 group-hover:rotate-12 transition-all"/>
              </div>
            </Link>

            <div className="grid grid-cols-2 divide-x divide-white/10 max-h-[70vh] overflow-y-auto">
              {/* LEFT — Signature Tools */}
              <div className="p-4 bg-[radial-gradient(circle_at_top_left,rgba(212,164,55,0.06),rgba(0,0,0,0)_60%)]">
                <div className="flex items-center justify-between mb-2 px-1">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[color:var(--kc-gold)]">Signature Tools</div>
                  {enabledFeatures.length > 0 && <div className="text-[9px] tracking-[0.18em] uppercase text-white/40">New</div>}
                </div>
                {enabledFeatures.length === 0 ? (
                  <div className="px-3 py-6 text-center text-xs text-white/40">
                    Tools coming soon.
                  </div>
                ) : (
                  <div className="space-y-1">
                    {enabledFeatures.map((it) => (
                      <Row
                        key={it.slug}
                        Icon={it.Icon}
                        label={it.label}
                        desc={it.desc}
                        to={it.to}
                        accent="gold"
                        testid={`resources-feature-${it.slug}`}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* RIGHT — Library (every item from the Resources page) */}
              <div className="p-4">
                <div className="flex items-center justify-between mb-2 px-1">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-white/50">Library</div>
                  {libraryItems.length > 0 && <div className="text-[9px] tracking-[0.18em] uppercase text-white/30">{libraryItems.length} items</div>}
                </div>
                {libraryItems.length === 0 ? (
                  <div className="px-3 py-6 text-center text-xs text-white/40">Loading library…</div>
                ) : (
                  <div className="space-y-1">
                    {libraryItems.map((it) => (
                      <Row
                        key={it.key}
                        Icon={it.Icon}
                        label={it.label}
                        desc={it.desc}
                        to={it.href}
                        external={it.external}
                        testid={`resources-item-${(it.label || '').replace(/\s+/g, '-').toLowerCase()}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer strip */}
            <div className="px-4 py-2.5 bg-black/40 border-t border-white/10 flex items-center justify-between text-[10px] tracking-[0.18em] uppercase">
              <span className="text-white/40">Tools that train the mind.</span>
              <Link to="/resources" onClick={() => setOpen(false)} className="text-[color:var(--kc-gold)] hover:underline" data-testid="resources-menu-footer-link">Browse all →</Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourcesMenu;
